import { useEffect, useId, useRef, useState } from "react";
import { framed } from "../../src/frame.ts";
import { dueSoonCount } from "../../src/waits.ts";
import {
  api,
  type BillingSummary,
  type DeletedTrip,
  type Me,
  type PassCard,
  type TripCard,
  type TripList,
  type TripsHeard,
} from "./api.ts";
import { type Land, loadLand } from "./coast.ts";
import { early } from "./early.ts";
import { FirstTrip, forgetFirstTripCopied, TripIsHere } from "./FirstTrip.tsx";
import { failureOf } from "./failure.ts";
import { openFeedback } from "./feedback.ts";
import { lastDay, localToday, tripDates } from "./format.ts";
import { useLoad } from "./hooks.ts";
import { useFolds } from "./kept.ts";
import { usePersonEvents } from "./me-live.ts";
import { InstallHint, NoSignal, useInstallHint } from "./NoSignal.tsx";
import { NotifyHint, useNotifyHint } from "./Notifications.tsx";
import { useAgain } from "./offline.ts";
import { Opening, TripsFailed } from "./opening.tsx";
import { buyPass, needsPass } from "./paying.ts";
import { HaveACode } from "./Redeem.tsx";
import { RefundDialog, refundLine } from "./Refund.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { keptKey } from "./reopen.ts";
import { navigate, rememberInStep, stepState, tripPath } from "./router.ts";
import { type StartMode, StartTrip } from "./StartTrip.tsx";
import { Avatar, AvatarStack, Button, Icon } from "./souvenir.ts";
import { MenuItem } from "./TripMenu.tsx";
import { Badge, ButtonLink, Count, factLine, ItemIcon, Wordmark } from "./ui.tsx";
import { ViewersAsk } from "./ViewersAsk.tsx";

// My trips as my event stream says them: which, whether I'm in or asking, and their last change; with
// the Trip Passes waiting as placeholders and my plan (features/paying.md).
const marks = ({ trips, passes, plan }: TripsHeard) =>
  [
    ...trips.map((t) => `${t.id}:${t.status}:${t.status === "member" ? t.updated_at : ""}`).sort(),
    ...passes.map((p) => `pass:${p}`).sort(),
    `plan:${plan}`,
  ].join(" ");
const shown = (list: TripList): TripsHeard => ({
  trips: list.trips,
  passes: list.passes.map((p) => p.id),
  plan: list.billing.plan,
});

export function Trips({ me }: { me: Me }) {
  // Back online (or once this phone keeps them), or behind what my event stream says: load them again.
  const [behind, setBehind] = useState(0);
  const trips = useLoad(() => early("trips", api.trips), [useAgain(), behind]);
  // Your trips hears about trips as they appear (features/live-updates.md): one my AI just made, one I've
  // been let into, a change to one. Each (re)connect says them all; loaded again only when they differ
  // from what's shown, once for each thing heard.
  const [heard, setHeard] = useState<string | null>(null);
  const asked = useRef<string | null>(null);
  // When this page last heard my AI calling Tripmo: the wait for a first trip says it's working on it.
  const [activeAt, setActiveAt] = useState<number | null>(null);
  usePersonEvents({ trips: (now) => setHeard(marks(now)), active: () => setActiveAt(Date.now()) });
  useEffect(() => {
    if (heard === null || trips.state !== "ready" || asked.current === heard) return;
    if (marks(shown(trips.data)) === heard) return;
    asked.current = heard;
    setBehind((n) => n + 1);
  }, [heard, trips]);
  // One suggestion card at a time, the first that applies: installing (on a phone; on an iPhone,
  // notifications need it), then notifications.
  const install = useInstallHint();
  const notify = useNotifyHint();
  // Settled once every card knows whether it would show (notifications reads this device first): until
  // then the card shown may still change.
  const cardsReady = notify.show !== null;
  // A trip I just deleted (Trip settings brought me here): "Deleted … · Undo", for a moment. Said once:
  // the step forgets it, so coming back here doesn't say it again.
  const [justDeleted, setJustDeleted] = useState(() => stepState().deleted ?? null);
  useEffect(() => {
    if (stepState().deleted) rememberInStep({ deleted: undefined });
  }, []);
  useEffect(() => {
    document.title = "Your trips · Tripmo";
  }, []);
  const reload = () => setBehind((n) => n + 1);
  const folds = useFolds(keptKey("folds", me.user.id));
  // Starting a trip here: from a placeholder (its pass) or New trip (my plan). A link may ask for a
  // placeholder's dialog (?start=<pass>, from the page after buying one): opened once the pass is shown,
  // and the address put back, so Back and a reload don't open it again.
  const [starting, setStarting] = useState<StartMode | null>(null);
  const startAsked = useRef(new URLSearchParams(location.search).get("start"));
  useEffect(() => {
    const asked = startAsked.current;
    if (!asked || trips.state !== "ready") return;
    startAsked.current = null;
    navigate("/", { replace: true });
    if (trips.data.passes.some((p) => p.id === asked)) setStarting({ kind: "pass", pass: asked });
  }, [trips]);
  const billing = trips.state === "ready" ? trips.data.billing : null;
  const newTrip = billing && billing.plan !== "none" ? () => setStarting({ kind: "plan", billing }) : null;
  // Your first trip (features/getting-started.md): before someone's first trip (none made, asked for or
  // deleted, and none opened before: someone who left their trips is long past it), Your trips is the
  // first trip's prompt and its wait, once they have an AI to send it to. The trip it was waiting for, the
  // moment it's here: said so, with its card and Open your trip, until opened (or the page is opened again).
  const noAi = me.ais.length === 0;
  // Requests of mine that ended while away (turned down) or here (turned down live, or taken back from their card):
  // each said once, as a card, for as long as this page shows (features/trips-and-members.md, "Withdraw a join
  // request, and hear a turn-down"); never again after, here or on another device.
  const [ended, setEnded] = useState<Ended[]>([]);
  const turnedDown = trips.state === "ready" ? trips.data.turned_down : [];
  const turnedKey = turnedDown.map((t) => t.id).join(" ");
  // biome-ignore lint/correctness/useExhaustiveDependencies: once for each new turn-down heard
  useEffect(() => {
    if (turnedDown.length === 0) return;
    setEnded((was) => [
      ...turnedDown
        .filter((t) => !was.some((e) => e.id === t.id))
        .map((t) => ({ id: t.id, title: t.title, owner: t.owner, how: "turned_down" as const })),
      ...was,
    ]);
    api.turnDownsSeen(turnedDown.map((t) => t.id)).catch(() => {});
  }, [turnedKey]);
  const withdrawn = (trip: TripCard) => {
    setEnded((was) => [
      { id: trip.id, title: trip.title, owner: trip.owner?.name ?? "The trip's owner", how: "withdrawn" },
      ...was.filter((e) => e.id !== trip.id),
    ]);
    reload();
  };
  // A trip I'm asking for again (or let into) is its own card again.
  const endedShown = ended.filter((e) => trips.state !== "ready" || !trips.data.trips.some((t) => t.id === e.id));
  const none =
    endedShown.length === 0 &&
    turnedDown.length === 0 &&
    !noAi &&
    trips.state === "ready" &&
    trips.data.trips.length === 0 &&
    trips.data.deleted.length === 0 &&
    trips.data.passes.length === 0 &&
    !me.tips_seen.includes("first_trip");
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    if (none) setWaited(true);
  }, [none]);
  const here = waited && trips.state === "ready" ? trips.data.trips.find((t) => t.status === "member") : undefined;
  useEffect(() => {
    if (here) forgetFirstTripCopied();
  }, [here]);
  // Opened, by this or any other route, the trip's page puts the first trip's standing out away (on
  // every device), as it loads.
  const open = (trip: TripCard) => navigate(tripPath(trip.id));
  // Without an AI (connecting skipped) and no trip of their own: how to start one, once, until Not now
  // (features/getting-started.md, "Skip for now"). Nothing else asks.
  const [connectAway, setConnectAway] = useState(() => me.tips_seen.includes("connect_to_start"));
  const connectLine =
    noAi &&
    !connectAway &&
    trips.state === "ready" &&
    trips.data.passes.length === 0 &&
    !trips.data.trips.some((t) => t.status === "member" && t.owner?.id === me.user.id);
  // My face's one colour outside a trip, the same as on the profile (features/profile.md): as the trips
  // have it now, so a first trip arriving colours it at once.
  const color = (trips.state === "ready" ? trips.data.color : me.user.color) ?? undefined;
  // Why my trips couldn't be loaded, when it's no signal or Tripmo not answering (said with Try again).
  const failure = trips.state === "failed" ? failureOf(trips.error) : null;
  return (
    <div className="tm-page">
      {/* Until my trips are in, the sunrise (features/opening.md). */}
      {trips.state === "loading" && <Opening />}
      <header className="tm-bar tm-trips-bar">
        <Wordmark />
        <span className="tm-grow" />
        {/* Send feedback, in plain sight (features/feedback.md, "Where"): "Feedback" beside my face, the icon alone on a
            phone. */}
        <Button
          variant="quiet"
          size="sm"
          className="tm-trips-feedback"
          aria-label="Send feedback"
          onClick={() => openFeedback({ kind: "bug" })}
        >
          <ItemIcon name="feedback" size={20} />
          <span className="tm-trips-feedback-label">Feedback</span>
        </Button>
        <a
          className="tm-me-link"
          href="/profile"
          aria-label="Your profile"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate("/profile");
          }}
        >
          <Avatar
            name={me.user.name}
            size={32}
            {...(color ? { color } : {})}
            {...(me.user.photo ? { src: me.user.photo } : {})}
          />
          <span className="callout tm-me-name">{me.user.name}</span>
        </a>
      </header>
      {none ? (
        <main className="tm-trips is-first" data-cards-ready="true">
          <NoSignal what="your trips" />
          <FirstTrip me={me} activeAt={activeAt} billing={billing} onNewTrip={newTrip} buyHref={buyPass("/")} />
        </main>
      ) : here ? (
        <main className="tm-trips is-first" data-cards-ready="true">
          <TripIsHere card={<Card trip={here} onOpen={open} />} onOpen={() => open(here)} />
        </main>
      ) : (
        <main className="tm-trips" data-cards-ready={cardsReady}>
          <div className="tm-trips-head">
            <h1 className="display-l">Your trips</h1>
            {newTrip && (
              <Button variant="primary" size="sm" icon="plus" className="tm-new-trip" onClick={newTrip}>
                New trip
              </Button>
            )}
            {billing && (
              <BuyPass billing={billing} passWaits={trips.state === "ready" && trips.data.passes.length > 0} />
            )}
          </div>
          {failure && trips.state === "failed" ? (
            <TripsFailed failure={failure} again={reload} retrying={trips.retrying === true} />
          ) : (
            <NoSignal what="your trips" />
          )}
          {connectLine ? (
            <ConnectToStart
              onDismiss={() => {
                setConnectAway(true);
                api.putTipAway("connect_to_start").catch(() => {});
              }}
            />
          ) : install.show ? (
            <InstallHint onDismiss={install.dismiss} />
          ) : (
            <NotifyHint hint={notify} />
          )}
          {/* Each trip shared to view that asks me whether viewers see my name, photo and ratings (features/sharing.md). */}
          {trips.state === "ready" &&
            trips.data.trips
              .filter((t) => t.viewers_ask)
              .map((t) => <ViewersAsk key={`ask-${t.id}`} trip={t.id} title={t.title} onAnswered={reload} />)}
          {trips.state === "failed" && !failure && <p className="body tm-muted">{trips.message}</p>}
          {trips.state === "ready" &&
            trips.data.trips.length === 0 &&
            trips.data.passes.length === 0 &&
            endedShown.length === 0 && (
              <p className="body tm-muted">
                {needsPass(trips.data.billing)
                  ? "Get a Trip Pass to start a trip, then start it here or ask your AI."
                  : newTrip
                    ? noAi
                      ? "No trips yet. Start one with New trip, and it shows up here."
                      : "No trips yet. Start one with New trip, or ask your AI to, and it shows up here."
                    : noAi
                      ? "No trips yet. A trip you're let into shows up here."
                      : "No trips yet. Ask your AI to start one, and it shows up here."}
              </p>
            )}
          {trips.state === "ready" &&
            (trips.data.trips.length > 0 || trips.data.passes.length > 0 || endedShown.length > 0) && (
              <div className="tm-trip-grid">
                {endedShown.map((e) => (
                  <AskEnded key={e.id} ended={e} />
                ))}
                {trips.data.passes.map((p) => (
                  <Placeholder
                    key={p.id}
                    pass={p}
                    onStart={() => setStarting({ kind: "pass", pass: p.id })}
                    onChanged={reload}
                  />
                ))}
                {trips.data.trips.map((t) =>
                  t.status === "member" ? (
                    <Card key={t.id} trip={t} onOpen={open} />
                  ) : (
                    <Asked key={t.id} trip={t} onWithdrawn={() => withdrawn(t)} />
                  ),
                )}
              </div>
            )}
          {trips.state === "ready" && trips.data.deleted.length > 0 && (
            <RecentlyDeleted
              trips={trips.data.deleted}
              folded={folds.folded("deleted", true)}
              onFold={(f) => folds.set("deleted", f)}
              onRestored={reload}
            />
          )}
        </main>
      )}
      {starting && <StartTrip mode={starting} onClose={() => setStarting(null)} onChanged={reload} />}
      {justDeleted && (
        <UndoDelete
          key={justDeleted.id}
          trip={justDeleted}
          onUndone={reload}
          onGone={() => setJustDeleted((d) => (d?.id === justDeleted.id ? null : d))}
        />
      )}
    </div>
  );
}

// For someone without an AI and no trip of their own: how to start one, as one line. Connect your AI opens
// connecting over the profile; Not now puts it away for good, on every device.
function ConnectToStart({ onDismiss }: { onDismiss: () => void }) {
  return (
    <aside className="tm-connect-start" aria-label="Plan your own trip">
      <p className="callout tm-connect-start-text">Plan your own trip by connecting your AI.</p>
      <div className="tm-connect-start-actions">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/profile?connect=1", { state: { layer: true } })}
        >
          Connect your AI
        </Button>
        <Button variant="quiet" size="sm" onClick={onDismiss}>
          Not now
        </Button>
      </div>
    </aside>
  );
}

// The trip just deleted, with Undo: it comes back as it was. For a moment; Recently deleted keeps it.
function UndoDelete({
  trip,
  onUndone,
  onGone,
}: {
  trip: { id: string; title: string };
  onUndone: () => void;
  onGone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gone = useRef(onGone);
  gone.current = onGone;
  useEffect(() => {
    if (error) return;
    const timer = setTimeout(() => gone.current(), 10_000);
    return () => clearTimeout(timer);
  }, [error]);
  const undo = async () => {
    setBusy(true);
    try {
      await api.restoreTrip(trip.id);
      onUndone();
      gone.current();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't restore it.");
    }
  };
  return (
    <div className="tm-live tm-live-passing tm-undo tm-live-floating tm-deleted-notice" role="status">
      <span className="callout">{error ? "Couldn't restore it: try Restore below." : `Deleted ${trip.title}`}</span>
      {error ? (
        <Button variant="primary" size="sm" onClick={() => gone.current()}>
          OK
        </Button>
      ) : (
        <Button variant="primary" size="sm" icon="replay" disabled={busy} onClick={undo}>
          {busy ? "Undoing…" : "Undo"}
        </Button>
      )}
    </div>
  );
}

// The trips I deleted that I can still bring back, each with its last day: after it, it's gone for good.
// Folded to one line by default (its name and how many), opened with a tap, and kept open or folded on
// this device like Bookings' folds (features/trips-and-members.md).
function RecentlyDeleted({
  trips,
  folded,
  onFold,
  onRestored,
}: {
  trips: DeletedTrip[];
  folded: boolean;
  onFold: (folded: boolean) => void;
  onRestored: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const restore = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await api.restoreTrip(id);
      onRestored();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't restore it. Try again.");
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className={`tm-choice tm-deleted${folded ? " is-folded" : ""}`} aria-labelledby="deleted-title">
      <h2 className="tm-choice-heading">
        <button
          type="button"
          className="tm-choice-head tm-deleted-head"
          aria-expanded={!folded}
          aria-controls="deleted-body"
          onClick={() => onFold(!folded)}
        >
          <span className="tm-choice-title">
            <span id="deleted-title" className="title">
              Recently deleted
              <Count n={trips.length} />
            </span>
          </span>
          <Icon name="chevron" size={18} className="tm-choice-caret" />
        </button>
      </h2>
      {!folded && (
        <div id="deleted-body" className="tm-deleted-body">
          <p className="callout tm-muted">
            Restore one with everyone and everything in it. After its last day, it's gone for good.
          </p>
          <ul className="tm-deleted-list">
            {trips.map((t) => (
              <li key={t.id} className="tm-deleted-row">
                <span className="tm-deleted-text">
                  <span className="body-strong">{t.title}</span>
                  <span className="caption tm-muted">Last day {lastDay(t.restorable_until)}</span>
                </span>
                <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => restore(t.id)}>
                  {busy === t.id ? "Restoring…" : "Restore"}
                </Button>
              </li>
            ))}
          </ul>
          {error && (
            <p className="callout tm-error" role="alert">
              {error}
              <ReportThis />
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// A card's facts, " · " between them, each whole on its line ("4 booked", "1 question" never break
// inside on a phone).
function Facts({ parts }: { parts: (string | null)[] }) {
  return factLine(parts);
}

// A trip's card: the whole card opens the trip, and says so ("Open"), with a pressed state. A person's
// first trip stands out until they've opened it (features/trips-and-members.md).
function Card({ trip, onOpen }: { trip: TripCard; onOpen: (trip: TripCard) => void }) {
  const dates = tripDates(trip.dates);
  const { places, stays, moves } = trip.counts;
  const some = (n: number, one: string, many: string) => (n ? `${n} ${n === 1 ? one : many}` : null);
  const facts = [
    some(places, "place", "places"),
    some(stays, "stay", "stays"),
    some(moves, "journey", "journeys"),
    places + stays + moves === 0 ? "Nothing planned yet" : null,
    trip.booked ? `${trip.booked} booked` : null,
    // As the trip's own notice says (Spots.tsx, LockedNotice): it can be read, not changed.
    trip.locked ? "Read-only" : null,
  ].filter(Boolean);
  // What waits on me in it (DESIGN.md, Views, "What waits on me leads"): what of mine or nobody's is due
  // within 7 days or overdue (by my own today), what waits on my rating, the questions waiting on me.
  const due = dueSoonCount(trip.waiting?.due ?? [], localToday());
  const rate = trip.waiting?.to_rate ?? 0;
  const questions = trip.waiting?.questions ?? 0;
  const waits = [
    due ? `${due} due this week` : null,
    rate ? `${rate} to rate` : null,
    some(questions, "question", "questions"),
  ].filter(Boolean);
  // Who's asking to join waits on the owner, but not on a read-only trip, which lets nobody in for now.
  const [first, ...more] = trip.locked ? [] : trip.requests;
  const href = tripPath(trip.id);
  return (
    <a
      className={`tm-trip${trip.first_unopened ? " is-first" : ""}`}
      href={href}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onOpen(trip);
      }}
    >
      <MiniMap points={trip.points} routes={trip.routes} />
      <div className="tm-trip-body">
        {dates ? (
          <span className="eyebrow tm-sun-text">{dates}</span>
        ) : (
          <span className="caption tm-muted">No dates yet</span>
        )}
        <span className="display-m">{trip.title}</span>
        <span className="callout tm-muted">
          <Facts parts={facts} />
        </span>
        {waits.length > 0 && (
          <span className="callout tm-trip-waits">
            <Facts parts={waits} />
          </span>
        )}
        {/* A line of its own, not in the foot, so the foot is the same on every card: faces and Open. */}
        {first && (
          <span className="callout tm-request">
            <Badge count={more.length + 1} />
            {more.length ? `${first.name} and ${more.length} more asked to join` : `${first.name} asked to join`}
          </span>
        )}
        {/* Offered to me to take over (DESIGN.md, Member, "Taking over a trip"): waiting on me, as a request waits on
            its owner. */}
        {trip.offer && !trip.locked && (
          <span className="callout tm-request">
            <Badge count={1} />
            {`${trip.offer.by} offers you this trip`}
          </span>
        )}
        <span className="tm-trip-foot">
          <AvatarStack people={trip.members} size={28} />
          <span className="tm-trip-open" aria-hidden="true">
            Open
            <Icon name="chevron" size={18} />
          </span>
        </span>
      </div>
    </a>
  );
}

// A Trip Pass waiting to be used (features/paying.md): in the grid like a trip's card, before them, with
// the empty map; the whole card starts the trip. While it can be refunded (or a refund is asked for), a
// "…" on its map opens its menu: Request a refund, or Withdraw the request.
function Placeholder({ pass, onStart, onChanged }: { pass: PassCard; onStart: () => void; onChanged: () => void }) {
  const request =
    pass.refund_request?.status === "open" || pass.refund_request?.status === "approved" ? pass.refund_request : null;
  return (
    <div className="tm-pass-card">
      <button type="button" className="tm-trip is-pass" data-pass={pass.id} onClick={onStart}>
        <span className="tm-trip-art is-empty" aria-hidden="true">
          <Icon name="map" size={32} />
        </span>
        <span className="tm-trip-body">
          <span className="eyebrow tm-sun-text">Trip Pass</span>
          {/* The sign-up gift's (features/paying.md, "A free first trip for every sign-up"). */}
          <span className="display-m">{pass.gift ? "Your first trip, on us" : "Your next trip"}</span>
          <span className="callout tm-muted">Ready to use · up to {pass.seats} people</span>
          {request && <span className="caption tm-muted">{refundLine(null, request)}</span>}
          <span className="tm-trip-foot">
            <span className="tm-trip-open">
              Start this trip
              <Icon name="chevron" size={18} />
            </span>
          </span>
        </span>
      </button>
      {(pass.refundable_until || request?.status === "open") && (
        <PassMenu pass={pass} request={request} onStart={onStart} onChanged={onChanged} />
      )}
    </div>
  );
}

// The placeholder's menu: Start this trip, and Request a refund while it can be (Withdraw while asked).
function PassMenu({
  pass,
  request,
  onStart,
  onChanged,
}: {
  pass: PassCard;
  request: PassCard["refund_request"];
  onStart: () => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  const items = () => [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
  // biome-ignore lint/correctness/useExhaustiveDependencies: set up each time it opens
  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Tab") {
        e.preventDefault();
        e.stopImmediatePropagation();
        close();
      }
    };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, [open]);
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at - 1);
  };
  const choose = (run: () => void) => {
    close(false);
    run();
  };
  const withdraw = async () => {
    if (!request) return;
    setError(null);
    try {
      await api.withdrawRefund(request.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        className={`tm-pass-more${open ? " is-open" : ""}`}
        aria-label="More for this Trip Pass"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open && (
        <>
          <div className="tm-menu-scrim" onPointerDown={() => close(false)} aria-hidden="true" />
          <div
            ref={menu}
            id={menuId}
            className="tm-trip-menu tm-pass-menu"
            role="menu"
            aria-label="Trip Pass"
            onKeyDown={onMenuKey}
          >
            <MenuItem icon={<Icon name="plus" size={20} />} onClick={() => choose(onStart)}>
              Start this trip
            </MenuItem>
            {request?.status === "open" ? (
              <MenuItem
                icon={<Icon name="close" size={20} />}
                note="A person at Tripmo hasn't decided yet"
                onClick={() => choose(withdraw)}
              >
                Withdraw refund request
              </MenuItem>
            ) : (
              pass.refundable_until && (
                <MenuItem
                  icon={<Icon name="import" size={20} />}
                  note={`Until ${lastDay(pass.refundable_until)}`}
                  onClick={() => choose(() => setAsking(true))}
                >
                  Request a refund
                </MenuItem>
              )
            )}
          </div>
        </>
      )}
      {error && (
        <p className="caption tm-error tm-pass-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      {asking && pass.refundable_until && (
        <RefundDialog
          target={{ type: "trip_pass", id: pass.id }}
          what="Trip Pass"
          offer={{ until: pass.refundable_until, why: "this pass hasn't been used" }}
          takesBack="The pass goes from Your trips."
          onClose={(changed) => {
            setAsking(false);
            if (changed) onChanged();
          }}
        />
      )}
    </>
  );
}

// Get a Trip Pass, under the heading, for someone without a plan (plan holders' trips are included),
// where this server sells them; "Have a code?" beside it, for a free one (features/paying.md, "Free Trip Passes from
// codes"). While an unused pass waits on Your trips (bought, from a code or the sign-up gift), "Have a code?" alone:
// another pass is bought when it's needed (DESIGN.md, Views, "Paying").
function BuyPass({ billing, passWaits }: { billing: BillingSummary; passWaits: boolean }) {
  if (billing.plan !== "none" || !billing.buying) return null;
  return (
    <div className={`tm-buy-pass-row${passWaits ? " is-code-only" : ""}`}>
      {!passWaits && (
        <ButtonLink href={buyPass("/")} variant="secondary" size="sm" className="tm-buy-pass">
          Get a Trip Pass · {billing.prices.trip_pass}
        </ButtonLink>
      )}
      <HaveACode />
    </div>
  );
}

// A request of mine that ended: turned down by the owner, or taken back from its card.
interface Ended {
  id: string;
  title: string;
  owner: string;
  how: "turned_down" | "withdrawn";
}

// A trip I've asked to join: who I'm waiting for, and taking my request back.
function Asked({ trip, onWithdrawn }: { trip: TripCard; onWithdrawn: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.withdrawRequest(trip.id);
      onWithdrawn();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Couldn't take the request back. Try again.");
    }
  };
  return (
    <div className="tm-trip is-asked">
      <div className="tm-trip-body">
        <span className="caption tm-muted">Asked to join</span>
        <span className="display-m">{trip.title}</span>
        <span className="callout tm-muted">
          Waiting for {trip.owner?.name ?? "the trip's owner"} to let you in. You'll see the trip here once they do.
        </span>
        <span className="tm-asked-foot">
          <Button variant="quiet" size="sm" disabled={busy} onClick={withdraw}>
            {busy ? "Withdrawing…" : "Withdraw request"}
          </Button>
        </span>
        {error && (
          <p className="caption tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
      </div>
    </div>
  );
}

// Said once, where its request was: the owner didn't let me in, or I took my request back. Gone next time.
function AskEnded({ ended }: { ended: Ended }) {
  return (
    <div className="tm-trip is-asked is-ended" role="status">
      <div className="tm-trip-body">
        <span className="caption tm-muted">{ended.how === "turned_down" ? "Not let in" : "Request taken back"}</span>
        <span className="display-m">{ended.title}</span>
        <span className="callout tm-muted">
          {ended.how === "turned_down"
            ? `${ended.owner} didn't let you in. If you think that's a mistake, check with them; their join link lets you ask again.`
            : `${ended.owner} won't see your request any more. Their join link lets you ask again.`}
        </span>
      </div>
    </div>
  );
}

// Where the trip goes, at a glance and without loading a map: land and sea from a coastline outline,
// the trip's places and stays as dots and its journeys as dashed lines, in the map's own projection
// (Web Mercator). Framed as the Map opens (frame.ts: where the trip stays and visits, so journeys from
// home run off the edge), and at least a region wide, so a city trip shows its coast.
const W = 320;
const H = 140;
// Pixels per degree at most: about 2.7 degrees across the card.
const MAX_SCALE = 120;
const mercatorY = (lat: number) => {
  const clamped = Math.max(-85, Math.min(85, lat));
  return (Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)) * 180) / Math.PI;
};
const latitudeOf = (y: number) => (Math.atan(Math.exp((y * Math.PI) / 180)) * 360) / Math.PI - 90;

function MiniMap({ points, routes }: { points: [number, number][]; routes: TripCard["routes"] }) {
  const [land, setLand] = useState<Land[]>([]);
  const ends = [...points, ...routes.flat()];
  useEffect(() => {
    if (ends.length === 0) return;
    let live = true;
    loadLand().then((l) => live && setLand(l));
    return () => {
      live = false;
    };
  }, [ends.length]);
  if (ends.length === 0) {
    return (
      <div className="tm-trip-art is-empty" aria-hidden="true">
        <Icon name="map" size={32} />
      </div>
    );
  }
  const frame = framed({ points, routes });
  const xs = frame.map((p) => p[0]);
  const ys = frame.map((p) => mercatorY(p[1]));
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  // One scale for both axes, keeping what's in the trip inside 260 x 100 of the card.
  const scale = Math.min(260 / Math.max(x1 - x0, 1e-9), 100 / Math.max(y1 - y0, 1e-9), MAX_SCALE);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const project = ([lng, lat]: [number, number]): [number, number] => [
    W / 2 + (lng - cx) * scale,
    H / 2 - (mercatorY(lat) - cy) * scale,
  ];
  // What the card shows, in degrees, to skip land that's out of it.
  const west = cx - W / 2 / scale;
  const east = cx + W / 2 / scale;
  const south = latitudeOf(cy - H / 2 / scale);
  const north = latitudeOf(cy + H / 2 / scale);
  let path = "";
  for (const polygon of land) {
    const [w, s, e, n] = polygon.box;
    if (e < west || w > east || n < south || s > north) continue;
    for (const ring of polygon.rings) {
      let last: [number, number] | null = null;
      for (const p of ring) {
        const [x, y] = project(p);
        // Points closer than half a pixel add nothing.
        if (last && Math.abs(x - last[0]) < 0.5 && Math.abs(y - last[1]) < 0.5) continue;
        path += `${last ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
        last = [x, y];
      }
      path += "Z";
    }
  }
  return (
    <svg className="tm-trip-art" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect className="tm-art-sea" width={W} height={H} />
      {path && <path className="tm-art-land" d={path} fillRule="evenodd" />}
      {routes.map(([from, to], i) => {
        const [ax, ay] = project(from);
        const [bx, by] = project(to);
        // biome-ignore lint/suspicious/noArrayIndexKey: routes have no identity of their own
        return <line key={i} className="tm-art-route" x1={ax} y1={ay} x2={bx} y2={by} />;
      })}
      {points.slice(0, 60).map((p, i) => {
        const [x, y] = project(p);
        // biome-ignore lint/suspicious/noArrayIndexKey: points have no identity of their own
        return <circle key={i} cx={x} cy={y} r={5} className="tm-art-dot" />;
      })}
    </svg>
  );
}
